import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { confirmDialog } from 'primereact/confirmdialog';
import { toast } from 'react-toastify';
import moment from 'moment';
import classNames from 'classnames';

// Redux & APIs
import { filteredHrListApi } from '../../redux/apis/hr';
import {
  getJobHrAssignmentsApi,
  assignJobHrApi,
  getSingleJobHrAssignmentApi,
  updateJobHrAssignmentDurationApi,
  relieveJobHrAssignmentApi
} from '../../redux/apis/jobs';

// Constants
import {
  HR_ALLOCATION_INITIAL_FORM_ERRORS,
  HR_ALLOCATION_VALIDATION_MESSAGES
} from '../../constants/common';

// Components & Utils
import CustomButton from '../CustomButton';
import Label from '../Label';
import AvtarImage from '../../pages/profile/AvtarImage';
import { isDarkMode, wordCapitalize } from '../../utils/common';

const JobHrAllocationModal = ({ visible, onHide, job, onRefresh }) => {
  const isDark = isDarkMode();
  const currentJobId = job?.ID;

  const [allocations, setAllocations] = useState([]);
  const [hrsList, setHrsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [addEditModalVisible, setAddEditModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAllocationId, setSelectedAllocationId] = useState(null);
  const [selectedHrId, setSelectedHrId] = useState(null);
  const [selectedHrName, setSelectedHrName] = useState('');
  const [formStartDate, setFormStartDate] = useState(new Date());
  const [durationEndDate, setDurationEndDate] = useState(null);
  const [formErrors, setFormErrors] = useState(HR_ALLOCATION_INITIAL_FORM_ERRORS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      filteredHrListApi().then((res) => {
        if (res?.status === 200) {
          const list = res?.data?.data
            ?.map((hr) => ({
              id: hr?.ID || hr?.id,
              name: `${wordCapitalize(hr?.FirstName || '')} ${wordCapitalize(
                hr?.LastName || ''
              )}`.trim(),
              firstName: hr?.FirstName,
              lastName: hr?.LastName,
              email: hr?.Email,
              isActive: hr?.isActive,
              userProfile: hr?.UserProfile
            }))
            ?.sort((a, b) => {
              if (a.isActive !== b.isActive) return b.isActive ? 1 : -1;
              return a.name.localeCompare(b.name);
            });
          setHrsList(list || []);
        }
      });
    }
  }, [visible]);

  const fetchAllocations = async () => {
    if (!currentJobId) return;
    setLoading(true);
    try {
      const res = await getJobHrAssignmentsApi(currentJobId);
      if (res?.status === 200 || res?.data?.status) {
        const raw = res?.data?.data || [];
        const formatted = raw.map((item) => {
          const hrName =
            item?.HRName ||
            (item?.FirstName
              ? `${wordCapitalize(item.FirstName)} ${wordCapitalize(item.LastName || '')}`.trim()
              : hr?.FirstName
              ? `${wordCapitalize(hr.FirstName)} ${wordCapitalize(hr.LastName || '')}`.trim()
              : 'HR Member');
          const startDate =
            item?.DurationStartDate || item?.StartDate
              ? new Date(item.DurationStartDate || item.StartDate)
              : new Date();
          const endDate = item?.DurationEndDate ? new Date(item.DurationEndDate) : null;

          return {
            id: item?.ID,
            jobId: item?.JobID || currentJobId,
            hrId: item?.HRUserID,
            hrName,
            firstName: item?.FirstName || '',
            lastName: item?.LastName || '',
            email: item?.Email || '',
            userProfile: item?.UserProfile,
            startDate,
            endDate,
            durationInDays: item?.DurationInDays,
            isActive: item?.IsActive !== false
          };
        });
        setAllocations(formatted);
      }
    } catch (err) {
      console.error('Failed to fetch HR assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && currentJobId) {
      fetchAllocations();
    }
  }, [visible, currentJobId]);

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setSelectedAllocationId(null);
    setSelectedHrId(null);
    setSelectedHrName('');

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setFormStartDate(today);

    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    setDurationEndDate(nextWeek);

    setFormErrors({ hr: '', startDate: '', duration: '' });
    setAddEditModalVisible(true);
  };

  const handleOpenEditModal = (allocation) => {
    setIsEditing(true);
    setSelectedAllocationId(allocation.id);
    setSelectedHrId(allocation.hrId);
    setSelectedHrName(allocation.hrName);

    const sDate = allocation.startDate ? new Date(allocation.startDate) : new Date();
    setFormStartDate(sDate);

    const eDate = allocation.endDate ? new Date(allocation.endDate) : null;
    setDurationEndDate(eDate);

    setFormErrors({ hr: '', startDate: '', duration: '' });
    setAddEditModalVisible(true);

    if (allocation.id && typeof allocation.id === 'string' && !allocation.id.startsWith('alloc_')) {
      getSingleJobHrAssignmentApi(allocation.id).then((res) => {
        if (res?.status === 200 && res?.data?.data) {
          const detail = res.data.data;
          if (detail.DurationStartDate || detail.StartDate) {
            setFormStartDate(new Date(detail.DurationStartDate || detail.StartDate));
          }
          if (detail.DurationEndDate) {
            setDurationEndDate(new Date(detail.DurationEndDate));
          }
          if (detail.HRName) {
            setSelectedHrName(detail.HRName);
          }
        }
      });
    }
  };

  const handleCloseAddEditModal = () => {
    setAddEditModalVisible(false);
    setSelectedAllocationId(null);
    setSelectedHrId(null);
    setSelectedHrName('');
    setFormStartDate(new Date());
    setDurationEndDate(null);
    setFormErrors(HR_ALLOCATION_INITIAL_FORM_ERRORS);
  };

  const validateForm = () => {
    const errors = { ...HR_ALLOCATION_INITIAL_FORM_ERRORS };
    let isValid = true;

    if (!isEditing && !selectedHrId) {
      errors.hr = HR_ALLOCATION_VALIDATION_MESSAGES.SELECT_HR;
      isValid = false;
    }

    if (!formStartDate) {
      errors.startDate = HR_ALLOCATION_VALIDATION_MESSAGES.SELECT_START_DATE;
      isValid = false;
    }

    if (!durationEndDate) {
      errors.duration = HR_ALLOCATION_VALIDATION_MESSAGES.SELECT_END_DATE;
      isValid = false;
    } else if (formStartDate && durationEndDate < formStartDate) {
      errors.duration = HR_ALLOCATION_VALIDATION_MESSAGES.INVALID_DURATION;
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSaveAllocation = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (isEditing) {
        const res = await updateJobHrAssignmentDurationApi(selectedAllocationId, {
          durationStartDate: formStartDate.toISOString(),
          durationEndDate: durationEndDate.toISOString()
        });

        if (res?.status === 200 || res?.data?.status) {
          toast.success(res?.data?.message, {
            position: toast.POSITION.BOTTOM_RIGHT
          });
          handleCloseAddEditModal();
          await fetchAllocations();
        }
      } else {
        const res = await assignJobHrApi({
          jobId: currentJobId,
          hrId: selectedHrId,
          durationStartDate: formStartDate.toISOString(),
          durationEndDate: durationEndDate.toISOString()
        });

        if (res?.status === 200 || res?.data?.status) {
          toast.success(res?.data?.message, {
            position: toast.POSITION.BOTTOM_RIGHT
          });
          handleCloseAddEditModal();
          await fetchAllocations();
          if (onRefresh) onRefresh();
        }
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || error?.message || 'Failed to save HR allocation';
      toast.error(errMsg, { position: toast.POSITION.BOTTOM_RIGHT });
    } finally {
      setSaving(false);
    }
  };

  const handleRelieveAllocation = (alloc) => {
    confirmDialog({
      header: 'Relieve HR',
      message: `Are you sure you want to relieve ${alloc.hrName} from this job?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          const res = await relieveJobHrAssignmentApi(alloc.id);
          if (res?.status === 200 || res?.status === 204 || res?.data?.code === 200) {
            toast.success(res?.data?.message, {
              position: toast.POSITION.BOTTOM_RIGHT
            });
            setAllocations((prev) => prev.filter((item) => item.id !== alloc.id));
            await fetchAllocations();
            if (onRefresh) onRefresh();
          }
        } catch (err) {
          const errMsg =
            err?.response?.data?.message || err?.message || 'Failed to relieve HR from job';
          toast.error(errMsg, { position: toast.POSITION.BOTTOM_RIGHT });
        }
      }
    });
  };

  const formatDurationDisplay = (startDate, endDate) => {
    if (!startDate) return '--';
    const s = moment(startDate).format('DD MMM YYYY');
    if (!endDate) {
      return `${s} – Present`;
    }
    const e = moment(endDate).format('DD MMM YYYY');
    const days = Math.max(1, moment(endDate).diff(moment(startDate), 'days'));
    return `${s} – ${e} (${days} ${days === 1 ? 'Day' : 'Days'})`;
  };

  const hrOptionTemplate = (option) => {
    if (!option) return null;
    return (
      <div className="hr-dropdown-option">
        <div className="flex align-items-center gap-2">
          <AvtarImage
            firstName={option.firstName}
            lastName={option.lastName}
            className="w-2rem h-2rem text-xs"
          />
          <div>
            <div className="hr-dropdown-option-name">{option.name}</div>
            {option.email && <div className="hr-dropdown-option-email">{option.email}</div>}
          </div>
        </div>
        <span
          className={classNames('hr-status-dot', {
            'active-dot': option.isActive,
            'inactive-dot': !option.isActive
          })}
        />
      </div>
    );
  };

  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);

  return (
    <>
      <Dialog
        visible={visible}
        onHide={onHide}
        header={
          <div className="hr-allocation-header-wrap">
            <div>
              <h3 className="hr-allocation-title">HR Allocation</h3>
              {job?.JobTitle && <span className="hr-allocation-subtitle">{job.JobTitle}</span>}
            </div>
            <CustomButton
              variant="contained"
              onClick={handleOpenAddModal}
              className="primary-btn hr-add-btn">
              <i className="pi pi-plus mr-2" />
              Add HR
            </CustomButton>
          </div>
        }
        className={classNames('hr-allocation-modal', { 'dark-theme': isDark })}
        closable
        draggable={false}
        resizable={false}>
        <div className="hr-allocation-content">
          {loading ? (
            <div className="hr-spinner-wrapper">
              <ProgressSpinner className="hr-spinner" />
            </div>
          ) : allocations.length === 0 ? (
            <div className="hr-empty-state">
              <div className="hr-empty-icon">
                <i className="pi pi-users" />
              </div>
              <h4 className="hr-empty-title">No HR added</h4>
              <p className="hr-empty-desc">No active HRs are allocated to this job yet.</p>
              <CustomButton
                variant="outlined"
                onClick={handleOpenAddModal}
                className="outline-btn hr-empty-btn">
                <i className="pi pi-plus mr-1" />
                Add HR
              </CustomButton>
            </div>
          ) : (
            <div className="allocation-list-container">
              <div className="hr-allocation-list-header">
                <div className="hr-col-name">HR Name</div>
                <div className="hr-col-duration">Duration</div>
                <div className="hr-col-action">Action</div>
              </div>

              <div className="hr-allocation-list flex flex-column gap-2">
                {allocations.map((alloc) => (
                  <div key={alloc.id} className="hr-allocation-item">
                    <div className="hr-col-name-content">
                      <AvtarImage
                        firstName={alloc.firstName}
                        lastName={alloc.lastName}
                        className="w-2rem h-2rem text-xs"
                      />
                      <div className="hr-member-name">{alloc.hrName}</div>
                    </div>

                    <div className="hr-col-duration-content">
                      <div className="hr-duration-text">
                        <i className="pi pi-calendar hr-calendar-icon" />
                        <span>{formatDurationDisplay(alloc.startDate, alloc.endDate)}</span>
                      </div>
                    </div>

                    <div className="hr-col-action-content">
                      <button
                        type="button"
                        onClick={() => handleOpenEditModal(alloc)}
                        title="Edit HR Duration"
                        className="hr-edit-btn">
                        <i className="pi pi-pencil" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRelieveAllocation(alloc)}
                        title="Relieve HR from Job"
                        className="hr-delete-btn">
                        <i className="pi pi-trash" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        visible={addEditModalVisible}
        onHide={handleCloseAddEditModal}
        header={
          <div className="flex align-items-center justify-content-between w-full">
            <h4 className="hr-submodal-title">{isEditing ? 'Edit HR' : 'Add HR'}</h4>
          </div>
        }
        className={classNames('hr-add-edit-modal', { 'dark-theme': isDark })}
        closable
        draggable={false}
        resizable={false}>
        <div className="form-box-wrapper hr-submodal-form">
          <div className="custom-form-group hr-form-group-mb">
            <Label text="HR" isMandatory />
            {isEditing ? (
              <InputText
                value={selectedHrName || 'HR Member'}
                disabled
                readOnly
                className="w-full disabled-input"
              />
            ) : (
              <Dropdown
                value={selectedHrId}
                options={hrsList}
                onChange={(e) => {
                  setSelectedHrId(e.value);
                  if (formErrors.hr) setFormErrors({ ...formErrors, hr: '' });
                }}
                optionLabel="name"
                optionValue="id"
                filter
                filterBy="name,email"
                placeholder="Select HR"
                className={classNames('w-full', { 'p-invalid': !!formErrors.hr })}
                itemTemplate={hrOptionTemplate}
              />
            )}
            {formErrors.hr && <small className="p-error">{formErrors.hr}</small>}
          </div>

          <div className="custom-form-group hr-form-group-mb">
            <Label text="Duration Start Date" isMandatory />
            <Calendar
              value={formStartDate}
              onChange={(e) => {
                setFormStartDate(e.value);
                if (formErrors.startDate) setFormErrors({ ...formErrors, startDate: '' });
                if (durationEndDate && e.value && durationEndDate < e.value) {
                  setDurationEndDate(null);
                }
              }}
              dateFormat="dd/mm/yy"
              showIcon
              readOnlyInput
              placeholder="Select duration start date"
              className={classNames('w-full', { 'p-invalid': !!formErrors.startDate })}
            />
            {formErrors.startDate && <small className="p-error">{formErrors.startDate}</small>}
          </div>

          <div className="custom-form-group hr-form-group-mb-lg">
            <Label text="Duration End Date" isMandatory />
            <Calendar
              value={durationEndDate}
              onChange={(e) => {
                setDurationEndDate(e.value);
                if (formErrors.duration) setFormErrors({ ...formErrors, duration: '' });
              }}
              minDate={formStartDate || todayDate}
              dateFormat="dd/mm/yy"
              showIcon
              readOnlyInput
              placeholder="Select duration end date"
              className={classNames('w-full', { 'p-invalid': !!formErrors.duration })}
            />
            {formErrors.duration && <small className="p-error">{formErrors.duration}</small>}
          </div>

          <div className="form-btn-wrapper justify-flex-end flex gap-2">
            <CustomButton
              variant="contained"
              onClick={handleCloseAddEditModal}
              color="error"
              className="gray-btn"
              disabled={saving}>
              Cancel
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={handleSaveAllocation}
              disabled={saving}
              className="primary-btn">
              {saving ? 'Saving...' : isEditing ? 'Update' : 'Add HR'}
            </CustomButton>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default JobHrAllocationModal;
